import clsx from "clsx";
import React from "react";
import { withRefProp } from "../../../utils/withRefProp";
import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import LazyList from "../LazyList/LazyList";
import styles from "./ItemList.module.scss";

export interface ItemListProps<T> extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  ref?: React.Ref<HTMLDivElement>;
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  getItemKey: (item: T, index: number) => string | number;
  renderEmpty?: () => React.ReactNode;
  header?: string;
  virtualizeThreshold?: number;
  onReachEnd?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  reachEndOffset?: number;
  skeletonRows?: number;
  renderSkeletonItem?: (index: number) => React.ReactNode;
}

const DEFAULT_VIRTUALIZE_THRESHOLD = 25;

const ItemList = withRefProp(
  <T,>({
    items = [] as T[],
    renderItem,
    getItemKey,
    renderEmpty,
    className,
    header,
    virtualizeThreshold = DEFAULT_VIRTUALIZE_THRESHOLD,
    onReachEnd,
    hasMore = false,
    loadingMore = false,
    reachEndOffset = 180,
    skeletonRows = 0,
    renderSkeletonItem,
    ref,
    ...props
  }: ItemListProps<T>) => {
    return (
      <LazyList
        ref={ref}
        className={clsx(styles.itemList, className)}
        scrollClassName={styles.itemsContainer}
        items={items}
        getItemKey={getItemKey}
        renderItem={renderItem}
        renderEmpty={renderEmpty}
        virtualizeThreshold={virtualizeThreshold}
        onReachEnd={onReachEnd}
        hasMore={hasMore}
        loadingMore={loadingMore}
        reachEndOffset={reachEndOffset}
        skeletonRows={skeletonRows}
        renderSkeletonItem={renderSkeletonItem}
        {...props}
      >
        {header && <div className={styles.header}>{header}</div>}
      </LazyList>
    );
  },
);

export interface ItemListItemProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  selected?: boolean;
  loading?: boolean;
  disabled?: boolean;
  as?: "button" | "div";
}

const ItemListItem = ({
  children,
  className,
  selected,
  loading,
  disabled,
  as = "button",
  ...props
}: ItemListItemProps) => {
  const isDisabled = loading || disabled;
  if (as === "div") {
    return (
      <div
        aria-disabled={isDisabled || undefined}
        className={clsx(styles.item, className)}
        data-selected={selected ? "true" : "false"}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={clsx(styles.item, className)}
      data-selected={selected ? "true" : "false"}
      disabled={isDisabled}
      {...props}
    >
      {children}
    </button>
  );
};

export interface ItemListLeftProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const ItemListLeft = ({ children, className, ...props }: ItemListLeftProps) => {
  return (
    <div
      className={clsx(styles.itemLeft, className)}
      {...props}
    >
      {children}
    </div>
  );
};

export interface ItemListRightProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const ItemListRight = ({ children, className, ...props }: ItemListRightProps) => {
  return (
    <div
      className={clsx(styles.itemRight, className)}
      {...props}
    >
      {children}
    </div>
  );
};

export interface ItemListTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const ItemListTitle = ({ children, className, ...props }: ItemListTitleProps) => {
  return (
    <div
      className={clsx(styles.itemTitle, className)}
      {...props}
    >
      {children}
    </div>
  );
};

export interface ItemListCollapsibleSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  title: string;
  defaultCollapsed?: boolean;
  onAdd?: () => void;
}

const ItemListCollapsibleSection = ({
  children,
  className,
  title,
  defaultCollapsed = false,
  onAdd,
  ...props
}: ItemListCollapsibleSectionProps) => {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
  const toggleCollapsed = React.useCallback(() => setCollapsed((s) => !s), []);

  return (
    <div
      className={clsx(styles.collapsibleSection, className)}
      {...props}
    >
      <div
        className={styles.collapsibleHeader}
        onClick={toggleCollapsed}
      >
        <div className={styles.collapsibleTitle}>{title}</div>
        <div className={styles.collapsibleActions}>
          {onAdd && (
            <Button
              icon
              label="Add"
              onClick={(e) => {
                e.stopPropagation();
                onAdd();
              }}
              className={styles.addButton}
            >
              <Icon name="add" />
            </Button>
          )}
          <Button
            icon
            label={collapsed ? "Expand" : "Collapse"}
            onClick={(e) => {
              e.stopPropagation();
              toggleCollapsed();
            }}
            className={styles.collapseButton}
          >
            {collapsed ? <Icon name="expand-section" /> : <Icon name="collapse-section" />}
          </Button>
        </div>
      </div>
      {!collapsed && <div className={styles.collapsibleContent}>{children}</div>}
    </div>
  );
};

export default Object.assign(ItemList, {
  Item: ItemListItem,
  Left: ItemListLeft,
  Right: ItemListRight,
  Title: ItemListTitle,
  CollapsibleSection: ItemListCollapsibleSection,
});
